import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import './AdminUserProfilePage.css';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {getLeagueByRating} from '../../../utils/rating/getLeagueByRating';
import {getFormattedPrice} from '../../../utils/price/getFormattedPrice';
import MatchHistoryItem from '../../MatchHistoryItem/MatchHistoryItem';
import {MINUS_SIGN} from "../../../constants/text";
import PlayerFactionIcon from '../../PlayerFactionIcon/PlayerFactionIcon';
import {FACTION_CONFIGS} from '../../../configs/factions';
import {getFactionSkinDisplayProps} from '../../../utils/factions/getFactionSkinDisplayProps';
import {copyTextToClipboard} from '../../../utils/other/copyTextToClipboard';
import {PRIMARY_COLOR} from '../../../styles/constants';
import QuestsButton, {useHasIncompleteQuests} from '../../elements/QuestsButton/QuestsButton';
import {getProfileAuthIdentity} from '../../../utils/auth/userAuthIdentity';
import {AI_ICON_PATH, AI_PLAYER_PROFILE_DESCRIPTION} from '../../../configs/aiMatch';
import AiPlayerMark from '../../VisibleBotMark/AiPlayerMark';

import FloatingBackButton from '../../elements/FloatingBackButton/FloatingBackButton';

const PAGE_ID = 'AdminUserProfilePage';

function formatDate(isoString, includeTime = true) {
  if (!isoString) return '—';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('ru-RU', {day: 'numeric', month: 'long', year: 'numeric'});
  if (!includeTime) return datePart;
  const time = date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute: '2-digit'});
  return `${datePart}, ${time}`;
}

// Дата окончания премиума: как в CrystalsExchangeModal — если осталось меньше
// суток, с точностью до минуты, иначе достаточно точности до дня
function formatPremiumExpiresAt(premiumExpiresAt) {
  const expires = new Date(premiumExpiresAt);
  const isLessThanDayLeft = expires.getTime() - Date.now() < 24 * 60 * 60 * 1000;
  if (isLessThanDayLeft) {
    return expires.toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
  return expires.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'});
}

function getMostPlayedFaction(factionsStatistics) {
  if (!factionsStatistics || typeof factionsStatistics !== 'object') return null;
  const entries = Object.entries(factionsStatistics).filter(([, stats]) => stats && typeof stats.gamesCount === 'number');
  if (entries.length === 0) return null;

  return entries.reduce((best, current) => {
    if (current[1].gamesCount > best[1].gamesCount) return current;
    if (current[1].gamesCount === best[1].gamesCount) {
      const currentDate = current[1].lastPlayedAt ? new Date(current[1].lastPlayedAt).getTime() : 0;
      const bestDate = best[1].lastPlayedAt ? new Date(best[1].lastPlayedAt).getTime() : 0;
      return currentDate > bestDate ? current : best;
    }
    return best;
  });
}

function AdminUserProfilePage({
  onQuestsClick,
  onRatingCrystalsClick,
  onCollectionClick,
                                isOwnProfile,
                                isPublicView,
                                userId,
                                userName,
                                onBack,
                                onMainMenu,
                                onMatchClick,
                                onAllMatchesClick,
                                isSuperAdmin,
                                onGrantAssetsClick,
                                onChangeNicknameClick,
                                onPurchaseHistoryClick,
                                onChestOpeningHistoryClick,
                                onDailyLoginRewardHistoryClick,
                                onPartnerClick,
                                onInviteFriendClick,
                                onInvitedFriendsClick,
                                onBuyPremiumClick,
                              }) {
  const isAdmin = useSelector((state) => state.private.auth.isAdmin);
  // Есть ли что выполнять прямо сейчас — от этого зависит место кнопки
  // «Задания» на странице (вид кнопки она выбирает себе сама)
  const hasIncompleteQuests = useHasIncompleteQuests();
  const isDev = process.env.NODE_ENV === 'development';
  const showPrivateInfo = isOwnProfile || isAdmin || isDev;
  const showUserId = isAdmin;

  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchesError, setMatchesError] = useState(null);
  // Какое значение только что скопировано: ID или почта / логин Яндекса
  const [copiedField, setCopiedField] = useState(null);

  usePageAnalytics('AdminUserProfilePage');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const url = isOwnProfile
          ? `${API_BASE}/users/me/profile`
          : isAdmin
            ? `${API_BASE}/admin/users/${userId}`
            : isPublicView
              ? `${API_BASE}/users/${userId}/public`
              : `${API_BASE}/admin/users/${userId}`;
        const res = await fetch(url, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch user profile');
        const data = await res.json();
        setProfile(data.user);
      } catch (e) {
        console.error('Admin user profile error', e);
        setError('Не удалось загрузить профиль пользователя');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, isOwnProfile, isPublicView, isAdmin]);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const res = await fetch(`${API_BASE}/users/${userId}/matches?limit=3&page=1`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch match history');
        const data = await res.json();
        setMatches(data.matches || []);
      } catch (e) {
        console.error('Match history error', e);
        setMatchesError('Не удалось загрузить историю матчей');
      } finally {
        setMatchesLoading(false);
      }
    };

    fetchMatches();
  }, [userId]);

  const handleCopy = async (text, field) => {
    try {
      // С фолбэком на execCommand — navigator.clipboard недоступен на
      // мобильных по http (дев-сборка по IP)
      await copyTextToClipboard(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 2000);
    } catch (e) {
      console.error(`Failed to copy user ${field}`, e);
    }
  };

  const isLoadingSuccess = !loading && !error && profile
  // Почта или логин Яндекса — поля есть только в админском ответе профиля
  const authIdentity = showUserId && isLoadingSuccess ? getProfileAuthIdentity(profile) : null;

  let averageRankedMedianPlaceDeviation = 0;
  let averageRankedMedianPlaceDeviationClassName = 'text-win-points';
  let averageRankedMedianPlaceDeviationSign = '';
  // Для приватной ветки (свой профиль/админ) — считаем сами из сырой даты
  // (premiumExpiresAt приходит только в этих двух случаях). В публичном
  // профиле сервер уже присылает готовый isPremiumActive, дату не отдает
  let isPremiumActive = false;
  if (isLoadingSuccess) {
    isPremiumActive = Boolean(profile.premiumExpiresAt) && new Date(profile.premiumExpiresAt).getTime() > Date.now();
    averageRankedMedianPlaceDeviation = profile.averageRankedMedianPlaceDeviation ?? 0

    if (averageRankedMedianPlaceDeviation > 0) {
      averageRankedMedianPlaceDeviationClassName = 'text-success'
      averageRankedMedianPlaceDeviationSign = '+'
    } else if (averageRankedMedianPlaceDeviation < 0) {
      averageRankedMedianPlaceDeviationClassName = 'text-rating'
      averageRankedMedianPlaceDeviationSign = MINUS_SIGN
    }
  }

  // Задания — только в своем профиле: чужие задания не показываем. Пока есть
  // невыполненные задания, кнопка привлекает внимание (цвет как у «Зала
  // славы» и значок восклицательного знака) и висит вверху страницы, над
  // основной информацией; когда все выполнено — обычная кнопка в общем списке
  const questsButton = isOwnProfile && onQuestsClick && (
    <QuestsButton onClick={onQuestsClick} pageId={PAGE_ID}/>
  );

  return (
    <>
      <FloatingBackButton onBack={onBack} pageId={PAGE_ID}/>

      <h1 className="admin-user-profile-title">{'Профиль'}</h1>

      <h1 className="admin-user-profile-name">
        {userName}
        {/* Синтетический игрок (бот): пометка видна только админам/суперадминам —
            обычный публичный профиль признак бота не содержит вовсе */}
        {(isAdmin || isSuperAdmin) && profile?.isBot && (
          <img
            className="admin-user-profile-bot-icon"
            src="/img/icons/bot.png"
            alt="Бот"
            title="Синтетический игрок (бот)"
          />
        )}
        {/* ИИ-игрок промо-матча «Битва с AI»: признак публичный, метку видят
            все — в отличие от бота, его не скрывают */}
        {profile?.isAi && <AiPlayerMark className="admin-user-profile-ai-icon"/>}
      </h1>

      {loading && <p className="admin-user-profile-status">Загрузка...</p>}
      {error && <p className="admin-user-profile-status admin-user-profile-status_error">{error}</p>}

      {/* Кто играет за аккаунт ИИ — над основной карточкой, первым делом */}
      {isLoadingSuccess && profile.isAi && (
        <div className="admin-user-profile-card admin-user-profile-ai-card">
          <img
            className="admin-user-profile-ai-card-icon"
            src={AI_ICON_PATH}
            alt=""
            aria-hidden="true"
          />
          <p className="admin-user-profile-ai-card-text">{AI_PLAYER_PROFILE_DESCRIPTION}</p>
        </div>
      )}

      {isLoadingSuccess && (
        <div className="admin-user-profile-card">

          {(() => {
            const premiumLabel = (
              <span className="admin-user-profile-label admin-user-profile-premium-label text-coins">
                Премиум
                <img
                  className="admin-user-profile-premium-icon"
                  src="/img/icons/premium.png"
                  alt="Премиум"
                />
              </span>
            );

            // Свой профиль: поле всегда на месте — либо дата, либо ссылка
            // «Купить» на вкладку премиума магазина
            if (isOwnProfile) {
              return (
                <div className="admin-user-profile-field">
                  {premiumLabel}
                  {isPremiumActive ? (
                    <span className="admin-user-profile-value text-coins">
                      Активен до {formatPremiumExpiresAt(profile.premiumExpiresAt)}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="admin-user-profile-buy-premium-link text-coins"
                      onClick={onBuyPremiumClick}
                    >
                      Купить
                    </button>
                  )}
                </div>
              );
            }

            // Чужой профиль (админ или публичный просмотр): поле показывается,
            // только если премиум сейчас действует — иначе просто не рисуем его
            if (showPrivateInfo) {
              return isPremiumActive && (
                <div className="admin-user-profile-field">
                  {premiumLabel}
                  <span className="admin-user-profile-value text-coins">
                    {formatPremiumExpiresAt(profile.premiumExpiresAt)}
                  </span>
                </div>
              );
            }

            return profile.isPremiumActive && (
              <div className="admin-user-profile-field">
                <span className="admin-user-profile-value admin-user-profile-premium-badge">
                  <img
                    className="admin-user-profile-premium-icon"
                    src="/img/icons/premium.png"
                    alt="Премиум"
                  />
                  Премиум
                </span>
              </div>
            );
          })()}
          <div className="admin-user-profile-field">
            <span className="admin-user-profile-label">Рейтинг</span>
            {(() => {
              const league = getLeagueByRating(profile.rating);
              return (
                <span className="admin-user-profile-value admin-user-profile-rating text-rating">
                  {profile.rating}
                  <img
                    className="admin-user-profile-league-icon"
                    src={league.iconPath}
                    alt={league.name}
                    title={league.name}
                  />
                </span>
              );
            })()}
          </div>
          {/* Очки коллекции — публичная величина, как и рейтинг: по ним строится
              раздел «Лучшие коллекции». В ответе без поля (сервер старой
              версии) показываем 0: getFormattedPrice(undefined) бросает */}
          <div className="admin-user-profile-field">
            <span className="admin-user-profile-label">Очки коллекции</span>
            <span className="admin-user-profile-value text-attention">
              {getFormattedPrice(profile.collectionPoints ?? 0)}
            </span>
          </div>
          {showPrivateInfo && (
            <div className="admin-user-profile-field">
              <span className="admin-user-profile-label">Баланс</span>
              <div className="admin-user-profile-balance">
                <div className="admin-user-profile-balance-item">
                  <span className="admin-user-profile-value text-coins-dark">{profile.numens ?? '—'}</span>
                  <img
                    className="admin-user-profile-balance-icon"
                    src="/img/icons/numens.png"
                    alt="Нумены"
                  />
                </div>
                <div className="admin-user-profile-balance-item">
                  <span className="admin-user-profile-value text-crystals">{profile.crystals ?? '—'}</span>
                  <img
                    className="admin-user-profile-balance-icon"
                    src="/img/icons/crystal.png"
                    alt="Кристаллы"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="admin-user-profile-field">
            <span className="admin-user-profile-label">Игры: всего / рейтинг / обычные</span>
            <span
              className="admin-user-profile-value"
            >{`${profile.gamesPlayed ?? 0} / ${profile.rankedGamesPlayed ?? 0} / ${profile.casualGamesPlayed ?? 0}`}</span>
          </div>
          <div className="admin-user-profile-field">
            <span
              className="admin-user-profile-label"
              title="Фракция, за которую игрок сыграл больше всего игр"
            >Любимая фракция</span>
            {(() => {
              const mostPlayed = getMostPlayedFaction(profile.factionsStatistics);
              if (!mostPlayed) return <span className="admin-user-profile-value">—</span>;
              const [factionId] = mostPlayed;
              const factionConfig = FACTION_CONFIGS.find((config) => config.id === factionId);
              const factionColor = factionConfig?.colors?.[0] || '#ffffff';
              const selectedSkin = profile.factionLoadouts?.[factionId]?.selectedSkin;
              const displayProps = getFactionSkinDisplayProps(factionConfig, selectedSkin);
              return (
                <span className="admin-user-profile-value admin-user-profile-most-played-faction">
                  <PlayerFactionIcon
                    player={{color: factionColor}}
                    factionId={factionId}
                    skinId={selectedSkin}
                    className="admin-user-profile-faction-icon"
                  />
                  <span style={{color: displayProps.textColor || factionConfig?.textColor || '#ffffff'}}>
                    {factionConfig?.name || factionId}
                  </span>
                </span>
              );
            })()}
          </div>
          <div className="admin-user-profile-field">
            <span className="admin-user-profile-label">{`Место в рейтинговых играх\n(cреднее от медианы)`}</span>
            <span
              className={`admin-user-profile-value ${averageRankedMedianPlaceDeviationClassName}`}
              title="Среднее отклонение от медианного места за рейтинговые игры"
            >
              {averageRankedMedianPlaceDeviationSign}
              {Math.abs(averageRankedMedianPlaceDeviation).toFixed(2)}
            </span>
          </div>
          <div className="admin-user-profile-field">
            <span className="admin-user-profile-label">Дата регистрации</span>
            <span className="admin-user-profile-value">{formatDate(profile.createdAt, false)}</span>
          </div>
          {!isOwnProfile && (
            <div className="admin-user-profile-field">
              <span className="admin-user-profile-label">Последний вход</span>
              <span className="admin-user-profile-value">{formatDate(profile.lastOnlineAt)}</span>
            </div>
          )}
          {isAdmin && (
            <div className="admin-user-profile-field">
              <span className="admin-user-profile-label">Согласие с документами</span>
              <span className="admin-user-profile-value">{formatDate(profile.legalDocumentsAcceptedAt)}</span>
            </div>
          )}
          {showUserId && (
            <div className="admin-user-profile-field">
              <span className="admin-user-profile-label">ID</span>
              <button
                className="admin-user-profile-id"
                onClick={() => handleCopy(userId, 'id')}
                type="button"
                title="Нажмите, чтобы скопировать"
              >
                {userId}
              </button>
              {copiedField === 'id' && <span className="admin-user-profile-copied">Скопировано!</span>}
            </div>
          )}
          {authIdentity && (
            <div className="admin-user-profile-field admin-user-profile-auth-identity">
              <span className="admin-user-profile-label">{authIdentity.label}</span>
              {authIdentity.value ? (
                <button
                  className="admin-user-profile-id"
                  onClick={() => handleCopy(authIdentity.value, 'authIdentity')}
                  type="button"
                  title="Нажмите, чтобы скопировать"
                >
                  {authIdentity.value}
                </button>
              ) : (
                <span className="admin-user-profile-auth-identity-missing">{authIdentity.missingText}</span>
              )}
              {copiedField === 'authIdentity' && <span className="admin-user-profile-copied">Скопировано!</span>}
            </div>
          )}
        </div>
      )}

      {hasIncompleteQuests && questsButton && (
        <div className="main-menu-buttons admin-user-profile-quests-buttons">
          {questsButton}
        </div>
      )}

      {(
        <div className="match-history-section">
          <h2 className="match-history-title">Последние матчи</h2>
          {matchesLoading && <p className="admin-user-profile-status">Загрузка матчей...</p>}
          {matchesError && <p className="admin-user-profile-status admin-user-profile-status_error">{matchesError}</p>}
          {!matchesLoading && !matchesError && (
            <>
              <div className="match-history-list">
                {matches.length === 0 ? (
                  <p className="admin-user-profile-status">Пока нет сыгранных матчей</p>
                ) : (
                  matches.map((match, index) => (
                    <MatchHistoryItem
                      key={match._id || match.matchId}
                      match={match}
                      userId={userId}
                      onClick={() => onMatchClick?.(match.matchId)}
                      pageId={PAGE_ID}
                      buttonId={`match_${index}`}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="main-menu-buttons">
        {/* «Пригласи друга» — только в собственном профиле пользователя */}
        {isOwnProfile && onInviteFriendClick && (
          <PageButton
            onClick={onInviteFriendClick}
            pageId={PAGE_ID}
            buttonId="invite_friend"
            style={{backgroundColor: PRIMARY_COLOR}}
          >
            Пригласить друга
          </PageButton>
        )}
        {!hasIncompleteQuests && questsButton}
        {/* Чужую коллекцию можно посмотреть — свою открывают из главного меню */}
        {!isOwnProfile && onCollectionClick && (
          <PageButton
            onClick={onCollectionClick}
            pageId={PAGE_ID}
            buttonId="collection"
          >
            Посмотреть коллекцию
          </PageButton>
        )}
        {/* История ежедневных начислений кристаллов за рейтинг: свою видит сам
            игрок, чужую — админ, открывший профиль из админки */}
        {(isOwnProfile || isAdmin) && onRatingCrystalsClick && (
          <PageButton
            onClick={onRatingCrystalsClick}
            pageId={PAGE_ID}
            buttonId="rating_crystals"
          >
            Кристаллы за рейтинг
          </PageButton>
        )}
        <PageButton
          className="match-history-all-button"
          onClick={onAllMatchesClick}
          pageId={PAGE_ID}
          buttonId="all_matches"
        >
          Все матчи
        </PageButton>

        {/* Партнерская программа: до назначения — «Назначить партнером»
            (пустая форма), после — «Инфо о партнере» (просмотр/редактирование).
            Различаются по дате назначения из админского профиля */}
        {isAdmin && onPartnerClick && isLoadingSuccess && (
          <PageButton
            onClick={() => onPartnerClick(Boolean(profile.partnerActivatedAt))}
            pageId={PAGE_ID}
            buttonId="partner"
            className={'amdin-partner-button'}
          >
            {profile.partnerActivatedAt ? 'Инфо о партнере' : 'Назначить партнером'}
          </PageButton>
        )}
        {/* Приглашенные друзья пользователя — админ видит тот же экран
            приглашения, что и сам пользователь */}
        {isAdmin && onInvitedFriendsClick && (
          <PageButton
            onClick={onInvitedFriendsClick}
            pageId={PAGE_ID}
            buttonId="invited_friends"
          >
            Приглашенные друзья
          </PageButton>
        )}
        {isAdmin && (
          <PageButton
            onClick={onPurchaseHistoryClick}
            pageId={PAGE_ID}
            buttonId="purchase_history"
          >
            История покупок
          </PageButton>
        )}
        {isAdmin && (
          <PageButton
            onClick={onChestOpeningHistoryClick}
            pageId={PAGE_ID}
            buttonId="chest_opening_history"
          >
            История открытий сундуков
          </PageButton>
        )}
        {isAdmin && (
          <PageButton
            onClick={onDailyLoginRewardHistoryClick}
            pageId={PAGE_ID}
            buttonId="daily_login_reward_history"
          >
            История ежедневных наград
          </PageButton>
        )}
        {isSuperAdmin && (
          <PageButton
            onClick={onGrantAssetsClick}
            pageId={PAGE_ID}
            buttonId="grant_assets"
          >
            Начислить имущество
          </PageButton>
        )}
        {/* Боту ник не сменить — сервер вернет ошибку, его имя все равно
            восстановится из пула при рестарте (см. ensureBotUsersSeeded) */}
        {isSuperAdmin && !profile?.isBot && (
          <PageButton
            onClick={onChangeNicknameClick}
            pageId={PAGE_ID}
            buttonId="change_nickname"
          >
            Сменить никнейм
          </PageButton>
        )}
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
        {onMainMenu && (
          <PageButton onClick={onMainMenu} pageId={PAGE_ID} buttonId="main_menu">
            Главное меню
          </PageButton>
        )}
      </div>
    </>
  );
}

export default AdminUserProfilePage;
